import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileText, FolderOpen, MessageSquare, BarChart3, LogOut, Pencil, Trash2, Plus, X, Eye, EyeOff, Users, Handshake, Target } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiError(detail) {
  if (!detail) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

function ImagePreview({ url, alt }) {
  if (!url) return null;
  return (
    <div className="mt-2 rounded-lg overflow-hidden border border-white/10 h-24 w-40">
      <img src={url} alt={alt || "Preview"} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
    </div>
  );
}

function ImageUpload({ value, onChange, container, label, token }) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const hdrs = { "Content-Type": "multipart/form-data" };
      if (token) hdrs["Authorization"] = `Bearer ${token}`;
      const { data } = await axios.post(`${API}/admin/upload/${container}`, formData, {
        headers: hdrs,
        withCredentials: true,
      });
      onChange(data.url);
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur upload");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label className="font-body text-xs text-slate-400">{label || "Image"}</Label>
      <div className="flex gap-2">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="bg-slate-800/50 border-white/10 text-slate-50 flex-1"
          placeholder="URL ou uploader..."
        />
        <label className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg font-body text-xs font-medium cursor-pointer transition-all ${uploading ? 'bg-slate-700 text-slate-400' : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'}`}>
          {uploading ? "..." : "Upload"}
          <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
        </label>
      </div>
      <ImagePreview url={value} alt={label} />
    </div>
  );
}

function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      onLogin(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-slate-800/80 ring-1 ring-blue-500/30 p-1 overflow-hidden">
            <img src="/logo.png" alt="EDUCPEOPLE" className="w-full h-full object-contain" style={{ filter: 'brightness(1.8) saturate(1.4)' }} />
          </div>
          <h1 className="font-heading text-3xl font-light text-slate-50">Administration</h1>
          <p className="font-body text-sm text-slate-400 mt-2">EDUCPEOPLE Back-office</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-login-form">
          <div className="space-y-2">
            <Label className="font-body text-sm text-slate-300">Email</Label>
            <Input
              type="email"
              data-testid="admin-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="bg-slate-900/50 border-white/10 text-slate-50"
              placeholder="admin@educpeople.org"
            />
          </div>
          <div className="space-y-2">
            <Label className="font-body text-sm text-slate-300">Mot de passe</Label>
            <Input
              type="password"
              data-testid="admin-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="bg-slate-900/50 border-white/10 text-slate-50"
              placeholder="Mot de passe"
            />
          </div>
          {error && <p data-testid="admin-login-error" className="font-body text-sm text-red-400">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full bg-blue-500 hover:bg-blue-400 text-white py-3 rounded-full font-body font-medium transition-all disabled:opacity-50"
          >
            {loading ? "Connexion..." : "Se connecter"}
          </button>
        </form>
      </div>
    </div>
  );
}

function BlogEditor({ post, onSave, onCancel, token, authors }) {
  const [form, setForm] = useState(
    post || { title: "", slug: "", excerpt: "", content: "", category: "Actualites EDUCPEOPLE", image_url: "", author_id: "", is_published: true }
  );
  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div className="bg-slate-900/50 border border-white/10 rounded-2xl p-6 space-y-4" data-testid="blog-editor">
      <div className="flex justify-between items-center">
        <h3 className="font-heading text-lg text-slate-50">{post ? "Modifier l'article" : "Nouvel article"}</h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-50"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">Titre</Label>
          <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="bg-slate-800/50 border-white/10 text-slate-50" data-testid="blog-editor-title" />
        </div>
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">Slug</Label>
          <Input value={form.slug} onChange={(e) => set("slug", e.target.value)} className="bg-slate-800/50 border-white/10 text-slate-50" data-testid="blog-editor-slug" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">Categorie</Label>
          <select value={form.category} onChange={(e) => set("category", e.target.value)} className="w-full rounded-lg bg-slate-800/50 border border-white/10 text-slate-50 p-2 font-body text-sm" data-testid="blog-editor-category">
            {["Actualites EDUCPEOPLE", "Inclusion & Societe", "IA & Education", "Temoignages", "Evenements"].map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">Auteur</Label>
          <select value={form.author_id} onChange={(e) => set("author_id", e.target.value)} className="w-full rounded-lg bg-slate-800/50 border border-white/10 text-slate-50 p-2 font-body text-sm" data-testid="blog-editor-author">
            <option value="">-- Choisir un auteur --</option>
            {(authors || []).map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          {form.author_id && authors && (() => {
            const sel = authors.find(a => a.id === form.author_id);
            return sel?.photo_url ? <div className="flex items-center gap-2 mt-2"><img src={sel.photo_url} alt={sel.name} className="w-8 h-8 rounded-full object-cover" /><span className="font-body text-xs text-slate-400">{sel.name}</span></div> : null;
          })()}
        </div>
      </div>
      <div className="space-y-2">
        <ImageUpload value={form.image_url} onChange={(url) => set("image_url", url)} container="articles" label="Image de l'article" token={token} />
      </div>
      <div className="space-y-2">
        <Label className="font-body text-xs text-slate-400">Extrait</Label>
        <Input value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} className="bg-slate-800/50 border-white/10 text-slate-50" data-testid="blog-editor-excerpt" />
      </div>
      <div className="space-y-2">
        <Label className="font-body text-xs text-slate-400">Contenu</Label>
        <textarea
          value={form.content}
          onChange={(e) => set("content", e.target.value)}
          rows={8}
          className="w-full rounded-lg bg-slate-800/50 border border-white/10 text-slate-50 p-3 font-body text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          data-testid="blog-editor-content"
        />
      </div>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.is_published} onChange={(e) => set("is_published", e.target.checked)} className="rounded" />
          <span className="font-body text-sm text-slate-300">Publie</span>
        </label>
      </div>
      <button
        onClick={() => onSave(form)}
        data-testid="blog-editor-save"
        className="bg-blue-500 hover:bg-blue-400 text-white px-6 py-2.5 rounded-full font-body text-sm font-medium transition-all"
      >
        Enregistrer
      </button>
    </div>
  );
}

function ProjectEditor({ project, onSave, onCancel, token }) {
  const [form, setForm] = useState(
    project || { title: "", slug: "", context: "", objectives: "", target_audience: "", actions: "", results: "", kpis: [], image_url: "", partners: [], program_slug: "", is_featured: false }
  );
  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div className="bg-slate-900/50 border border-white/10 rounded-2xl p-6 space-y-4" data-testid="project-editor">
      <div className="flex justify-between items-center">
        <h3 className="font-heading text-lg text-slate-50">{project ? "Modifier le projet" : "Nouveau projet"}</h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-50"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">Titre</Label>
          <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="bg-slate-800/50 border-white/10 text-slate-50" data-testid="project-editor-title" />
        </div>
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">Slug</Label>
          <Input value={form.slug} onChange={(e) => set("slug", e.target.value)} className="bg-slate-800/50 border-white/10 text-slate-50" data-testid="project-editor-slug" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="font-body text-xs text-slate-400">Contexte</Label>
        <textarea value={form.context} onChange={(e) => set("context", e.target.value)} rows={3} className="w-full rounded-lg bg-slate-800/50 border border-white/10 text-slate-50 p-3 font-body text-sm resize-none focus:outline-none" data-testid="project-editor-context" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">Objectifs</Label>
          <textarea value={form.objectives} onChange={(e) => set("objectives", e.target.value)} rows={3} className="w-full rounded-lg bg-slate-800/50 border border-white/10 text-slate-50 p-3 font-body text-sm resize-none focus:outline-none" data-testid="project-editor-objectives" />
        </div>
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">Public cible</Label>
          <textarea value={form.target_audience} onChange={(e) => set("target_audience", e.target.value)} rows={3} className="w-full rounded-lg bg-slate-800/50 border border-white/10 text-slate-50 p-3 font-body text-sm resize-none focus:outline-none" data-testid="project-editor-audience" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="font-body text-xs text-slate-400">Actions realisees</Label>
        <textarea value={form.actions} onChange={(e) => set("actions", e.target.value)} rows={3} className="w-full rounded-lg bg-slate-800/50 border border-white/10 text-slate-50 p-3 font-body text-sm resize-none focus:outline-none" />
      </div>
      <div className="space-y-2">
        <Label className="font-body text-xs text-slate-400">Resultats</Label>
        <textarea value={form.results} onChange={(e) => set("results", e.target.value)} rows={3} className="w-full rounded-lg bg-slate-800/50 border border-white/10 text-slate-50 p-3 font-body text-sm resize-none focus:outline-none" />
      </div>
      <div className="space-y-2">
        <ImageUpload value={form.image_url} onChange={(url) => set("image_url", url)} container="projets" label="Image du projet" token={token} />
      </div>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.is_featured} onChange={(e) => set("is_featured", e.target.checked)} className="rounded" />
          <span className="font-body text-sm text-slate-300">Projet phare</span>
        </label>
      </div>
      <button onClick={() => onSave(form)} data-testid="project-editor-save" className="bg-blue-500 hover:bg-blue-400 text-white px-6 py-2.5 rounded-full font-body text-sm font-medium transition-all">
        Enregistrer
      </button>
    </div>
  );
}

function AuthorEditor({ author, onSave, onCancel, token }) {
  const [form, setForm] = useState(
    author || { name: "", photo_url: "", linkedin: "", bio: "" }
  );
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="bg-slate-900/50 border border-white/10 rounded-2xl p-6 space-y-4" data-testid="author-editor">
      <div className="flex justify-between items-center">
        <h3 className="font-heading text-lg text-slate-50">{author ? "Modifier l'auteur" : "Nouvel auteur"}</h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-50"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">Nom complet</Label>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-slate-800/50 border-white/10 text-slate-50" data-testid="author-editor-name" />
        </div>
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">LinkedIn</Label>
          <Input value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} className="bg-slate-800/50 border-white/10 text-slate-50" placeholder="https://linkedin.com/in/..." />
        </div>
      </div>
      <div className="space-y-2">
        <ImageUpload value={form.photo_url} onChange={(url) => set("photo_url", url)} container="auteurs" label="Photo de l'auteur" token={token} />
      </div>
      <div className="space-y-2">
        <Label className="font-body text-xs text-slate-400">Biographie courte</Label>
        <textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} rows={2} className="w-full rounded-lg bg-slate-800/50 border border-white/10 text-slate-50 p-3 font-body text-sm resize-none focus:outline-none" />
      </div>
      <button onClick={() => onSave(form)} data-testid="author-editor-save" className="bg-blue-500 hover:bg-blue-400 text-white px-6 py-2.5 rounded-full font-body text-sm font-medium transition-all">Enregistrer</button>
    </div>
  );
}

function PartnerEditor({ partner, onSave, onCancel, token }) {
  const [form, setForm] = useState(
    partner || { name: "", type: "institutional", website: "", description: "", logo_url: "", is_active: true }
  );
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="bg-slate-900/50 border border-white/10 rounded-2xl p-6 space-y-4" data-testid="partner-editor">
      <div className="flex justify-between items-center">
        <h3 className="font-heading text-lg text-slate-50">{partner ? "Modifier le partenaire" : "Nouveau partenaire"}</h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-50"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">Nom</Label>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-slate-800/50 border-white/10 text-slate-50" data-testid="partner-editor-name" />
        </div>
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">Type</Label>
          <select value={form.type} onChange={(e) => set("type", e.target.value)} className="w-full rounded-lg bg-slate-800/50 border border-white/10 text-slate-50 p-2 font-body text-sm" data-testid="partner-editor-type">
            <option value="institutional">Institutionnel</option>
            <option value="corporate">Entreprise</option>
            <option value="tech">Technologique</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">Site web</Label>
          <Input value={form.website} onChange={(e) => set("website", e.target.value)} className="bg-slate-800/50 border-white/10 text-slate-50" placeholder="https://..." />
        </div>
        <div className="space-y-2">
          <ImageUpload value={form.logo_url} onChange={(url) => set("logo_url", url)} container="partenaires" label="Logo" token={token} />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="font-body text-xs text-slate-400">Description</Label>
        <Input value={form.description} onChange={(e) => set("description", e.target.value)} className="bg-slate-800/50 border-white/10 text-slate-50" />
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} className="rounded" />
        <span className="font-body text-sm text-slate-300">Actif</span>
      </label>
      <button onClick={() => onSave(form)} data-testid="partner-editor-save" className="bg-blue-500 hover:bg-blue-400 text-white px-6 py-2.5 rounded-full font-body text-sm font-medium transition-all">Enregistrer</button>
    </div>
  );
}

function ProgramEditor({ program, onSave, onCancel, token }) {
  const [form, setForm] = useState(
    program || { title: "", slug: "", description: "", long_description: "", objective: "", target_audience: "", actions: [], key_figures: {}, icon: "Target", image_url: "", order: 0 }
  );
  const set = (k, v) => setForm({ ...form, [k]: v });
  const actionsStr = Array.isArray(form.actions) ? form.actions.join("\n") : "";
  return (
    <div className="bg-slate-900/50 border border-white/10 rounded-2xl p-6 space-y-4" data-testid="program-editor">
      <div className="flex justify-between items-center">
        <h3 className="font-heading text-lg text-slate-50">{program ? "Modifier le programme" : "Nouveau programme"}</h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-50"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">Titre</Label>
          <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="bg-slate-800/50 border-white/10 text-slate-50" />
        </div>
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">Slug</Label>
          <Input value={form.slug} onChange={(e) => set("slug", e.target.value)} className="bg-slate-800/50 border-white/10 text-slate-50" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="font-body text-xs text-slate-400">Description courte</Label>
        <Input value={form.description} onChange={(e) => set("description", e.target.value)} className="bg-slate-800/50 border-white/10 text-slate-50" />
      </div>
      <div className="space-y-2">
        <Label className="font-body text-xs text-slate-400">Description longue</Label>
        <textarea value={form.long_description} onChange={(e) => set("long_description", e.target.value)} rows={4} className="w-full rounded-lg bg-slate-800/50 border border-white/10 text-slate-50 p-3 font-body text-sm resize-none focus:outline-none" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">Objectif</Label>
          <textarea value={form.objective} onChange={(e) => set("objective", e.target.value)} rows={2} className="w-full rounded-lg bg-slate-800/50 border border-white/10 text-slate-50 p-3 font-body text-sm resize-none focus:outline-none" />
        </div>
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">Public cible</Label>
          <textarea value={form.target_audience} onChange={(e) => set("target_audience", e.target.value)} rows={2} className="w-full rounded-lg bg-slate-800/50 border border-white/10 text-slate-50 p-3 font-body text-sm resize-none focus:outline-none" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="font-body text-xs text-slate-400">Actions (une par ligne)</Label>
        <textarea value={actionsStr} onChange={(e) => set("actions", e.target.value.split("\n").filter(Boolean))} rows={4} className="w-full rounded-lg bg-slate-800/50 border border-white/10 text-slate-50 p-3 font-body text-sm resize-none focus:outline-none" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <ImageUpload value={form.image_url} onChange={(url) => set("image_url", url)} container="programmes" label="Image du programme" token={token} />
        </div>
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">Ordre d'affichage</Label>
          <Input type="number" value={form.order} onChange={(e) => set("order", parseInt(e.target.value) || 0)} className="bg-slate-800/50 border-white/10 text-slate-50 w-24" />
        </div>
      </div>
      <button onClick={() => onSave(form)} data-testid="program-editor-save" className="bg-blue-500 hover:bg-blue-400 text-white px-6 py-2.5 rounded-full font-body text-sm font-medium transition-all">Enregistrer</button>
    </div>
  );
}

function VolunteerEditor({ volunteer, onSave, onCancel, token }) {
  const [form, setForm] = useState(
    volunteer || { first_name: "", last_name: "", role: "", photo_url: "", linkedin: "", facebook: "", instagram: "", twitter: "", is_active: true, order: 0 }
  );
  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div className="bg-slate-900/50 border border-white/10 rounded-2xl p-6 space-y-4" data-testid="volunteer-editor">
      <div className="flex justify-between items-center">
        <h3 className="font-heading text-lg text-slate-50">{volunteer ? "Modifier le bénévole" : "Nouveau bénévole"}</h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-50"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">Prénom</Label>
          <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="bg-slate-800/50 border-white/10 text-slate-50" data-testid="volunteer-editor-firstname" />
        </div>
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">Nom</Label>
          <Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="bg-slate-800/50 border-white/10 text-slate-50" data-testid="volunteer-editor-lastname" />
        </div>
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">Rôle</Label>
          <Input value={form.role} onChange={(e) => set("role", e.target.value)} className="bg-slate-800/50 border-white/10 text-slate-50" placeholder="Animateur, Mentor..." data-testid="volunteer-editor-role" />
        </div>
      </div>
      <div className="space-y-2">
        <ImageUpload value={form.photo_url} onChange={(url) => set("photo_url", url)} container="benevoles" label="Photo" token={token} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">LinkedIn</Label>
          <Input value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} className="bg-slate-800/50 border-white/10 text-slate-50" placeholder="https://linkedin.com/in/..." />
        </div>
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">Facebook</Label>
          <Input value={form.facebook} onChange={(e) => set("facebook", e.target.value)} className="bg-slate-800/50 border-white/10 text-slate-50" placeholder="https://facebook.com/..." />
        </div>
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">Instagram</Label>
          <Input value={form.instagram} onChange={(e) => set("instagram", e.target.value)} className="bg-slate-800/50 border-white/10 text-slate-50" placeholder="https://instagram.com/..." />
        </div>
        <div className="space-y-2">
          <Label className="font-body text-xs text-slate-400">X (Twitter)</Label>
          <Input value={form.twitter} onChange={(e) => set("twitter", e.target.value)} className="bg-slate-800/50 border-white/10 text-slate-50" placeholder="https://twitter.com/..." />
        </div>
      </div>
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} className="rounded" />
          <span className="font-body text-sm text-slate-300">Actif</span>
        </label>
        <div className="space-y-0 flex items-center gap-2">
          <Label className="font-body text-xs text-slate-400">Ordre</Label>
          <Input type="number" value={form.order} onChange={(e) => set("order", parseInt(e.target.value) || 0)} className="bg-slate-800/50 border-white/10 text-slate-50 w-20" />
        </div>
      </div>
      <button onClick={() => onSave(form)} data-testid="volunteer-editor-save" className="bg-blue-500 hover:bg-blue-400 text-white px-6 py-2.5 rounded-full font-body text-sm font-medium transition-all">
        Enregistrer
      </button>
    </div>
  );
}

function AdminDashboard({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [posts, setPosts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [allPartners, setAllPartners] = useState([]);
  const [allPrograms, setAllPrograms] = useState([]);
  const [allAuthors, setAllAuthors] = useState([]);
  const [allPages, setAllPages] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [editingVolunteer, setEditingVolunteer] = useState(null);
  const [editingPartner, setEditingPartner] = useState(null);
  const [editingProgram, setEditingProgram] = useState(null);
  const [editingAuthor, setEditingAuthor] = useState(null);
  const [editingPage, setEditingPage] = useState(null);
  const [showNewPost, setShowNewPost] = useState(false);
  const [showNewProject, setShowNewProject] = useState(false);
  const [showNewVolunteer, setShowNewVolunteer] = useState(false);
  const [showNewPartner, setShowNewPartner] = useState(false);
  const [showNewProgram, setShowNewProgram] = useState(false);
  const [showNewAuthor, setShowNewAuthor] = useState(false);

  const headers = { Authorization: `Bearer ${user.token}` };

  const loadData = () => {
    axios.get(`${API}/admin/stats`, { headers, withCredentials: true }).then((r) => setStats(r.data)).catch(() => {});
    axios.get(`${API}/admin/blog`, { headers, withCredentials: true }).then((r) => setPosts(r.data)).catch(() => {});
    axios.get(`${API}/admin/projects`, { headers, withCredentials: true }).then((r) => setProjects(r.data)).catch(() => {});
    axios.get(`${API}/admin/contacts`, { headers, withCredentials: true }).then((r) => setContacts(r.data)).catch(() => {});
    axios.get(`${API}/admin/volunteers`, { headers, withCredentials: true }).then((r) => setVolunteers(r.data)).catch(() => {});
    axios.get(`${API}/admin/partners`, { headers, withCredentials: true }).then((r) => setAllPartners(r.data)).catch(() => {});
    axios.get(`${API}/admin/programs`, { headers, withCredentials: true }).then((r) => setAllPrograms(r.data)).catch(() => {});
    axios.get(`${API}/admin/authors`, { headers, withCredentials: true }).then((r) => setAllAuthors(r.data)).catch(() => {});
    axios.get(`${API}/admin/pages`, { headers, withCredentials: true }).then((r) => setAllPages(r.data)).catch(() => {});
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, []);

  const savePost = async (form) => {
    if (editingPost) {
      await axios.put(`${API}/admin/blog/${editingPost.id}`, form, { headers, withCredentials: true });
    } else {
      await axios.post(`${API}/admin/blog`, form, { headers, withCredentials: true });
    }
    setEditingPost(null);
    setShowNewPost(false);
    loadData();
  };

  const deletePost = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    await axios.delete(`${API}/admin/blog/${id}`, { headers, withCredentials: true });
    loadData();
  };

  const saveProject = async (form) => {
    if (editingProject) {
      await axios.put(`${API}/admin/projects/${editingProject.id}`, form, { headers, withCredentials: true });
    } else {
      await axios.post(`${API}/admin/projects`, form, { headers, withCredentials: true });
    }
    setEditingProject(null);
    setShowNewProject(false);
    loadData();
  };

  const deleteProject = async (id) => {
    if (!window.confirm("Supprimer ce projet ?")) return;
    await axios.delete(`${API}/admin/projects/${id}`, { headers, withCredentials: true });
    loadData();
  };

  const markRead = async (id) => {
    await axios.put(`${API}/admin/contacts/${id}/read`, {}, { headers, withCredentials: true });
    loadData();
  };

  const saveVolunteer = async (form) => {
    if (editingVolunteer) {
      await axios.put(`${API}/admin/volunteers/${editingVolunteer.id}`, form, { headers, withCredentials: true });
    } else {
      await axios.post(`${API}/admin/volunteers`, form, { headers, withCredentials: true });
    }
    setEditingVolunteer(null);
    setShowNewVolunteer(false);
    loadData();
  };

  const deleteVolunteer = async (id) => {
    if (!window.confirm("Supprimer ce bénévole ?")) return;
    await axios.delete(`${API}/admin/volunteers/${id}`, { headers, withCredentials: true });
    loadData();
  };

  const savePartner = async (form) => {
    if (editingPartner) {
      await axios.put(`${API}/admin/partners/${editingPartner.id}`, form, { headers, withCredentials: true });
    } else {
      await axios.post(`${API}/admin/partners`, form, { headers, withCredentials: true });
    }
    setEditingPartner(null);
    setShowNewPartner(false);
    loadData();
  };

  const deletePartner = async (id) => {
    if (!window.confirm("Supprimer ce partenaire ?")) return;
    await axios.delete(`${API}/admin/partners/${id}`, { headers, withCredentials: true });
    loadData();
  };

  const saveProgram = async (form) => {
    if (editingProgram) {
      await axios.put(`${API}/admin/programs/${editingProgram.id}`, form, { headers, withCredentials: true });
    } else {
      await axios.post(`${API}/admin/programs`, form, { headers, withCredentials: true });
    }
    setEditingProgram(null);
    setShowNewProgram(false);
    loadData();
  };

  const deleteProgram = async (id) => {
    if (!window.confirm("Supprimer ce programme ?")) return;
    await axios.delete(`${API}/admin/programs/${id}`, { headers, withCredentials: true });
    loadData();
  };

  const saveAuthor = async (form) => {
    if (editingAuthor) {
      await axios.put(`${API}/admin/authors/${editingAuthor.id}`, form, { headers, withCredentials: true });
    } else {
      await axios.post(`${API}/admin/authors`, form, { headers, withCredentials: true });
    }
    setEditingAuthor(null);
    setShowNewAuthor(false);
    loadData();
  };

  const deleteAuthor = async (id) => {
    if (!window.confirm("Supprimer cet auteur ?")) return;
    await axios.delete(`${API}/admin/authors/${id}`, { headers, withCredentials: true });
    loadData();
  };

  const savePage = async (slug, data) => {
    await axios.put(`${API}/admin/pages/${slug}`, data, { headers, withCredentials: true });
    setEditingPage(null);
    loadData();
  };

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    onLogout();
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-slate-900/50 border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-slate-800/80 ring-1 ring-blue-500/30 p-0.5 overflow-hidden">
              <img src="/logo.png" alt="EDUCPEOPLE" className="w-full h-full object-contain" style={{ filter: 'brightness(1.8) saturate(1.4)' }} />
            </div>
            <span className="font-heading text-lg font-semibold text-slate-50">Administration</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-body text-sm text-slate-400">{user.email}</span>
            <button onClick={handleLogout} data-testid="admin-logout" className="flex items-center gap-2 text-slate-400 hover:text-red-400 font-body text-sm transition-colors">
              <LogOut className="w-4 h-4" /> Deconnexion
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
            {[
              { label: "Articles", value: stats.blog_posts, icon: FileText },
              { label: "Projets", value: stats.projects, icon: FolderOpen },
              { label: "Messages", value: stats.contacts, icon: MessageSquare, badge: stats.unread_contacts },
              { label: "Partenaires", value: stats.partners, icon: BarChart3 },
              { label: "Bénévoles", value: stats.volunteers || 0, icon: Users },
              { label: "Auteurs", value: stats.authors || 0, icon: FileText },
            ].map((s) => (
              <div key={s.label} className="bg-slate-900/50 border border-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <s.icon className="w-4 h-4 text-blue-400" />
                  {s.badge > 0 && (
                    <span className="bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{s.badge}</span>
                  )}
                </div>
                <div className="font-body text-2xl font-bold text-slate-50">{s.value}</div>
                <div className="font-body text-xs text-slate-400">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="blog" data-testid="admin-tabs">
          <TabsList className="bg-slate-900/50 border border-white/10 mb-6">
            <TabsTrigger value="blog" className="font-body text-sm data-[state=active]:bg-blue-500 data-[state=active]:text-white">Articles</TabsTrigger>
            <TabsTrigger value="projects" className="font-body text-sm data-[state=active]:bg-blue-500 data-[state=active]:text-white">Projets</TabsTrigger>
            <TabsTrigger value="contacts" className="font-body text-sm data-[state=active]:bg-blue-500 data-[state=active]:text-white">Messages</TabsTrigger>
            <TabsTrigger value="volunteers" className="font-body text-sm data-[state=active]:bg-blue-500 data-[state=active]:text-white">Bénévoles</TabsTrigger>
            <TabsTrigger value="partners" className="font-body text-sm data-[state=active]:bg-blue-500 data-[state=active]:text-white">Partenaires</TabsTrigger>
            <TabsTrigger value="authors" className="font-body text-sm data-[state=active]:bg-blue-500 data-[state=active]:text-white">Auteurs</TabsTrigger>
            <TabsTrigger value="programs" className="font-body text-sm data-[state=active]:bg-blue-500 data-[state=active]:text-white">Programmes</TabsTrigger>
            <TabsTrigger value="pages" className="font-body text-sm data-[state=active]:bg-blue-500 data-[state=active]:text-white">Pages</TabsTrigger>
          </TabsList>

          {/* Blog Tab */}
          <TabsContent value="blog">
            {showNewPost || editingPost ? (
              <BlogEditor
                post={editingPost}
                onSave={savePost}
                onCancel={() => { setShowNewPost(false); setEditingPost(null); }}
                token={user.token}
                authors={allAuthors}
              />
            ) : (
              <div>
                <button onClick={() => setShowNewPost(true)} data-testid="admin-new-post" className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-5 py-2 rounded-full font-body text-sm font-medium mb-6">
                  <Plus className="w-4 h-4" /> Nouvel article
                </button>
                <div className="space-y-3">
                  {posts.map((p) => (
                    <div key={p.id} className="bg-slate-900/50 border border-white/10 rounded-xl p-4 flex items-center justify-between" data-testid={`admin-post-${p.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <h4 className="font-body text-sm font-medium text-slate-50 truncate">{p.title}</h4>
                          {p.is_published ? (
                            <Eye className="w-3 h-3 text-emerald-400 shrink-0" />
                          ) : (
                            <EyeOff className="w-3 h-3 text-slate-500 shrink-0" />
                          )}
                        </div>
                        <p className="font-body text-xs text-slate-500">{p.category} - {new Date(p.published_at).toLocaleDateString("fr-FR")}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button onClick={() => setEditingPost(p)} className="text-slate-400 hover:text-blue-400 p-1"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => deletePost(p.id)} className="text-slate-400 hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects">
            {showNewProject || editingProject ? (
              <ProjectEditor
                project={editingProject}
                onSave={saveProject}
                onCancel={() => { setShowNewProject(false); setEditingProject(null); }}
                token={user.token}
              />
            ) : (
              <div>
                <button onClick={() => setShowNewProject(true)} data-testid="admin-new-project" className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-5 py-2 rounded-full font-body text-sm font-medium mb-6">
                  <Plus className="w-4 h-4" /> Nouveau projet
                </button>
                <div className="space-y-3">
                  {projects.map((p) => (
                    <div key={p.id} className="bg-slate-900/50 border border-white/10 rounded-xl p-4 flex items-center justify-between" data-testid={`admin-project-${p.id}`}>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-body text-sm font-medium text-slate-50 truncate">{p.title}</h4>
                        <p className="font-body text-xs text-slate-500">{p.program_slug || "Non assigne"}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button onClick={() => setEditingProject(p)} className="text-slate-400 hover:text-blue-400 p-1"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => deleteProject(p.id)} className="text-slate-400 hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <div className="space-y-3">
              {contacts.length === 0 ? (
                <p className="font-body text-sm text-slate-400 py-8 text-center">Aucun message pour le moment.</p>
              ) : (
                contacts.map((c) => (
                  <div
                    key={c.id}
                    className={`bg-slate-900/50 border rounded-xl p-4 ${c.is_read ? "border-white/10" : "border-blue-500/30"}`}
                    data-testid={`admin-contact-${c.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-body text-sm font-medium text-slate-50">{c.name}</h4>
                        <p className="font-body text-xs text-slate-500">{c.email} - {c.subject}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-body text-xs text-slate-500">
                          {new Date(c.created_at).toLocaleDateString("fr-FR")}
                        </span>
                        {!c.is_read && (
                          <button onClick={() => markRead(c.id)} className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full font-body">
                            Marquer lu
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="font-body text-sm text-slate-400">{c.message}</p>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Volunteers Tab */}
          <TabsContent value="volunteers">
            {showNewVolunteer || editingVolunteer ? (
              <VolunteerEditor
                volunteer={editingVolunteer}
                onSave={saveVolunteer}
                onCancel={() => { setShowNewVolunteer(false); setEditingVolunteer(null); }}
                token={user.token}
              />
            ) : (
              <div>
                <button onClick={() => setShowNewVolunteer(true)} data-testid="admin-new-volunteer" className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-5 py-2 rounded-full font-body text-sm font-medium mb-6">
                  <Plus className="w-4 h-4" /> Nouveau bénévole
                </button>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {volunteers.map((v) => (
                    <div key={v.id} className="bg-slate-900/50 border border-white/10 rounded-xl p-4 flex items-center gap-4" data-testid={`admin-volunteer-${v.id}`}>
                      {v.photo_url ? (
                        <img src={v.photo_url} alt={v.first_name} className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                          <span className="font-heading text-lg text-blue-400">{v.first_name[0]}</span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-body text-sm font-medium text-slate-50">{v.first_name} {v.last_name}</h4>
                        <p className="font-body text-xs text-slate-500">{v.role || "Bénévole"} {!v.is_active && "(inactif)"}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setEditingVolunteer(v)} className="text-slate-400 hover:text-blue-400 p-1"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => deleteVolunteer(v.id)} className="text-slate-400 hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Partners Tab */}
          <TabsContent value="partners">
            {showNewPartner || editingPartner ? (
              <PartnerEditor partner={editingPartner} onSave={savePartner} onCancel={() => { setShowNewPartner(false); setEditingPartner(null); }} token={user.token} />
            ) : (
              <div>
                <button onClick={() => setShowNewPartner(true)} data-testid="admin-new-partner" className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-5 py-2 rounded-full font-body text-sm font-medium mb-6">
                  <Plus className="w-4 h-4" /> Nouveau partenaire
                </button>
                <div className="space-y-3">
                  {allPartners.map((p) => (
                    <div key={p.id} className="bg-slate-900/50 border border-white/10 rounded-xl p-4 flex items-center justify-between" data-testid={`admin-partner-${p.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-body text-sm font-medium text-slate-50">{p.name}</h4>
                          <span className="font-body text-xs bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded-full">{p.type}</span>
                          {!p.is_active && <span className="font-body text-xs text-red-400">(inactif)</span>}
                        </div>
                        <p className="font-body text-xs text-slate-500">{p.description}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button onClick={() => setEditingPartner(p)} className="text-slate-400 hover:text-blue-400 p-1"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => deletePartner(p.id)} className="text-slate-400 hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>


          {/* Authors Tab */}
          <TabsContent value="authors">
            {showNewAuthor || editingAuthor ? (
              <AuthorEditor author={editingAuthor} onSave={saveAuthor} onCancel={() => { setShowNewAuthor(false); setEditingAuthor(null); }} token={user.token} />
            ) : (
              <div>
                <button onClick={() => setShowNewAuthor(true)} data-testid="admin-new-author" className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-5 py-2 rounded-full font-body text-sm font-medium mb-6">
                  <Plus className="w-4 h-4" /> Nouvel auteur
                </button>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {allAuthors.map((a) => (
                    <div key={a.id} className="bg-slate-900/50 border border-white/10 rounded-xl p-4 flex items-center gap-4" data-testid={`admin-author-${a.id}`}>
                      {a.photo_url ? (
                        <img src={a.photo_url} alt={a.name} className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                          <span className="font-heading text-lg text-blue-400">{a.name[0]}</span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-body text-sm font-medium text-slate-50">{a.name}</h4>
                        <p className="font-body text-xs text-slate-500">{a.bio || "Auteur"}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setEditingAuthor(a)} className="text-slate-400 hover:text-blue-400 p-1"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => deleteAuthor(a.id)} className="text-slate-400 hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Programs Tab */}
          <TabsContent value="programs">
            {showNewProgram || editingProgram ? (
              <ProgramEditor program={editingProgram} onSave={saveProgram} onCancel={() => { setShowNewProgram(false); setEditingProgram(null); }} token={user.token} />
            ) : (
              <div>
                <button onClick={() => setShowNewProgram(true)} data-testid="admin-new-program" className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-5 py-2 rounded-full font-body text-sm font-medium mb-6">
                  <Plus className="w-4 h-4" /> Nouveau programme
                </button>
                <div className="space-y-3">
                  {allPrograms.map((p) => (
                    <div key={p.id} className="bg-slate-900/50 border border-white/10 rounded-xl p-4 flex items-center justify-between" data-testid={`admin-program-${p.id}`}>
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        {p.image_url && <img src={p.image_url} alt={p.title} className="w-12 h-12 rounded-lg object-cover" />}
                        <div>
                          <h4 className="font-body text-sm font-medium text-slate-50">{p.title}</h4>
                          <p className="font-body text-xs text-slate-500">{p.description?.substring(0, 80)}...</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button onClick={() => setEditingProgram(p)} className="text-slate-400 hover:text-blue-400 p-1"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => deleteProgram(p.id)} className="text-slate-400 hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Pages Tab */}
          <TabsContent value="pages">
            {editingPage ? (
              <div className="bg-slate-900/50 border border-white/10 rounded-2xl p-6 space-y-4" data-testid="page-editor">
                <div className="flex justify-between items-center">
                  <h3 className="font-heading text-lg text-slate-50">Modifier : {editingPage.title}</h3>
                  <button onClick={() => setEditingPage(null)} className="text-slate-400 hover:text-slate-50"><X className="w-5 h-5" /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-body text-xs text-slate-400">Titre</Label>
                    <Input value={editingPage.title} onChange={(e) => setEditingPage({...editingPage, title: e.target.value})} className="bg-slate-800/50 border-white/10 text-slate-50" />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-body text-xs text-slate-400">Sous-titre</Label>
                    <Input value={editingPage.subtitle || ""} onChange={(e) => setEditingPage({...editingPage, subtitle: e.target.value})} className="bg-slate-800/50 border-white/10 text-slate-50" />
                  </div>
                </div>
                <div className="space-y-4">
                  <Label className="font-body text-xs text-slate-400">Sections ({editingPage.sections?.length || 0})</Label>
                  {(editingPage.sections || []).map((sec, i) => (
                    <div key={i} className="bg-slate-800/30 border border-white/5 rounded-xl p-4 space-y-3">
                      <div className="flex justify-between items-center">
                        <Input
                          value={sec.heading}
                          onChange={(e) => {
                            const s = [...editingPage.sections];
                            s[i] = {...s[i], heading: e.target.value};
                            setEditingPage({...editingPage, sections: s});
                          }}
                          className="bg-slate-800/50 border-white/10 text-slate-50 font-medium"
                          placeholder="Titre de la section"
                        />
                        <button onClick={() => {
                          const s = editingPage.sections.filter((_, j) => j !== i);
                          setEditingPage({...editingPage, sections: s});
                        }} className="text-red-400 hover:text-red-300 ml-2 shrink-0"><Trash2 className="w-4 h-4" /></button>
                      </div>
                      <textarea
                        value={sec.body}
                        onChange={(e) => {
                          const s = [...editingPage.sections];
                          s[i] = {...s[i], body: e.target.value};
                          setEditingPage({...editingPage, sections: s});
                        }}
                        rows={6}
                        className="w-full rounded-lg bg-slate-800/50 border border-white/10 text-slate-50 p-3 font-body text-sm resize-none focus:outline-none"
                      />
                    </div>
                  ))}
                  <button
                    onClick={() => setEditingPage({...editingPage, sections: [...(editingPage.sections || []), {heading: "", body: ""}]})}
                    className="inline-flex items-center gap-2 text-blue-400 font-body text-sm"
                  >
                    <Plus className="w-4 h-4" /> Ajouter une section
                  </button>
                </div>
                <button onClick={() => savePage(editingPage.slug, editingPage)} data-testid="page-editor-save" className="bg-blue-500 hover:bg-blue-400 text-white px-6 py-2.5 rounded-full font-body text-sm font-medium transition-all">
                  Enregistrer
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="font-body text-xs text-slate-500 mb-4">Pages statiques du site (Mentions légales, Confidentialité...)</p>
                {allPages.map((p) => (
                  <div key={p.slug} className="bg-slate-900/50 border border-white/10 rounded-xl p-4 flex items-center justify-between" data-testid={`admin-page-${p.slug}`}>
                    <div>
                      <h4 className="font-body text-sm font-medium text-slate-50">{p.title}</h4>
                      <p className="font-body text-xs text-slate-500">/{p.slug} — {p.sections?.length || 0} sections</p>
                    </div>
                    <button onClick={() => setEditingPage({...p})} className="text-slate-400 hover:text-blue-400 p-1"><Pencil className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser({ ...r.data, token: "" }))
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <AdminLogin onLogin={(u) => setUser(u)} />;
  }

  return <AdminDashboard user={user} onLogout={() => setUser(null)} />;
}
